// Memória da visão escolhida (gestão ↔ cliente) na navegação 2026, por ABA.
// Guarda em `sessionStorage` — e não em `localStorage` — porque a visão é uma
// decisão da aba: entrar num cliente numa aba não pode arrastar as outras.
// O valor é carimbado com o `id_interno` de quem decidiu, então um segundo
// profissional que logue na MESMA aba não herda a visão do primeiro.
//
// Só DECISÃO é gravada aqui (o usuário entrou num cliente, voltou para gestão).
// Palpite — a heurística de rota, o guard do cliente final, o fallback de
// cliente órfão — aplica o contexto sem passar por `writeStoredContext`; ver
// `navigation/applyContext` no store e a seção "Decisão ≠ palpite" do README.
//
// Toda leitura falha FECHADA: sem `userId` identificado, com valor corrompido,
// com storage indisponível (modo restrito/iframe) ou com carimbo de outro
// usuário, devolve null e o chamador cai na heurística de rota.
import { NAV_CONTEXT } from '@modules/navegacao-v2/store/state'

export const NAV_CONTEXT_STORAGE_KEY = 'aawz.nav2026.context'

const VALID_CONTEXTS = new Set([NAV_CONTEXT.GESTAO, NAV_CONTEXT.CLIENTE])

/**
 * @param {Object} [options]
 * @param {number|string} [options.userId] sem ele a leitura devolve null
 * @returns {string|null}
 */
export const readStoredContext = ({ userId } = {}) => {
  if (!isIdentifiedUser(userId)) return null
  const entry = readEntry()
  if (!entry) return null
  const isForeign = String(entry.userId) !== String(userId)
  return isForeign ? null : entry.context
}

/**
 * @param {string} context
 * @param {Object} [options]
 * @param {number|string} [options.userId]
 * @returns {boolean}
 */
export const writeStoredContext = (context, { userId = null } = {}) => {
  if (!isValidContext(context)) return false
  if (!isIdentifiedUser(userId)) return false
  const storage = sessionStorageRef()
  if (!storage) return false
  try {
    storage.setItem(
      NAV_CONTEXT_STORAGE_KEY,
      JSON.stringify({ context, userId })
    )
    return true
  } catch {
    return false
  }
}

const readEntry = () => {
  const storage = sessionStorageRef()
  if (!storage) return null
  try {
    const serialized = storage.getItem(NAV_CONTEXT_STORAGE_KEY)
    if (!serialized) return null
    const entry = JSON.parse(serialized)
    if (!entry || !isValidContext(entry.context)) return null
    return entry
  } catch {
    return null
  }
}

const isValidContext = (context) => VALID_CONTEXTS.has(context)

const isIdentifiedUser = (userId) => {
  if (userId === undefined || userId === null || userId === '') return false
  return Number(userId) > 0
}

const sessionStorageRef = () => {
  try {
    return globalThis.sessionStorage || null
  } catch {
    return null
  }
}
